import React, { useCallback, useEffect, useId, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiChevronDown, FiMonitor, FiSmartphone, FiShield } from 'react-icons/fi';
import BASE_URL from '../../../config/config';
import { cerrarSesion } from '../../../auth/sesion';

const SISTEMAS_MOVILES = ['iOS', 'Android'];
// Tiempo que el botón de "cerrar todas" espera la confirmación
const ESPERA_CONFIRMACION_MS = 4000;

const UNIDADES = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

const haceCuanto = (fecha, idioma) => {
  const segundos = (new Date(fecha).getTime() - Date.now()) / 1000;
  const formato = new Intl.RelativeTimeFormat(idioma, { numeric: 'auto' });
  for (const [unidad, tamano] of UNIDADES) {
    if (Math.abs(segundos) >= tamano) return formato.format(Math.round(segundos / tamano), unidad);
  }
  return formato.format(0, 'minute');
};

/**
 * Acordeón del perfil con los navegadores que tienen sesión abierta o son de
 * confianza para el MFA. Permite cerrar la sesión de uno o de todos los demás.
 *
 * @param recarga  cambia cuando otra parte del perfil altera la lista (por
 *                 ejemplo, al dejar de recordar dispositivos)
 */
const DispositivosConectados = ({ token, recarga = 0 }) => {
  const { t, i18n } = useTranslation();
  const idRegion = useId();
  const [abierto, setAbierto] = useState(false);
  const [dispositivos, setDispositivos] = useState(null);
  const [procesando, setProcesando] = useState(null);
  const [confirmandoTodos, setConfirmandoTodos] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BASE_URL}/usuario/dispositivos`, { headers: { Authorization: token } });
      setDispositivos(data);
    } catch {
      setDispositivos([]);
      toast.error(t('perfil.dispositivos.errorCargar'));
    }
  }, [token, t]);

  useEffect(() => { cargar(); }, [cargar, recarga]);

  // Al abrirlo se refresca: el último acceso puede haber cambiado
  useEffect(() => { if (abierto) cargar(); }, [abierto, cargar]);

  useEffect(() => {
    if (!confirmandoTodos) return undefined;
    const temporizador = setTimeout(() => setConfirmandoTodos(false), ESPERA_CONFIRMACION_MS);
    return () => clearTimeout(temporizador);
  }, [confirmandoTodos]);

  const revocar = async (dispositivo) => {
    try {
      setProcesando(dispositivo.id);
      await axios.delete(`${BASE_URL}/usuario/dispositivos/${dispositivo.id}`, { headers: { Authorization: token } });
      // El token de este navegador ya no vale: se sale al login sin aviso de caducidad
      if (dispositivo.actual) {
        cerrarSesion({ caducada: false });
        return;
      }
      setDispositivos((lista) => lista.filter((d) => d.id !== dispositivo.id));
      toast.success(t('perfil.dispositivos.revocado'));
    } catch {
      toast.error(t('perfil.dispositivos.errorRevocar'));
    } finally {
      setProcesando(null);
    }
  };

  const revocarTodos = async () => {
    if (!confirmandoTodos) {
      setConfirmandoTodos(true);
      return;
    }
    try {
      setProcesando('todos');
      setConfirmandoTodos(false);
      await axios.delete(`${BASE_URL}/usuario/dispositivos`, { headers: { Authorization: token } });
      setDispositivos((lista) => lista.filter((d) => d.actual));
      toast.success(t('perfil.dispositivos.revocadosTodos'));
    } catch {
      toast.error(t('perfil.dispositivos.errorRevocar'));
    } finally {
      setProcesando(null);
    }
  };

  const formatoFecha = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });
  const otros = (dispositivos || []).filter((d) => !d.actual).length;

  return (
    <div className={`perfil-dispositivos${abierto ? ' perfil-dispositivos--abierto' : ''}`}>
      <button
        type="button"
        className="perfil-dispositivos-cabecera"
        aria-expanded={abierto}
        aria-controls={idRegion}
        onClick={() => setAbierto((v) => !v)}
      >
        <span className="perfil-dispositivos-resumen">
          {dispositivos === null
            ? t('comun.cargando')
            : t('perfil.dispositivos.resumen', { count: dispositivos.length })}
        </span>
        <span className="perfil-dispositivos-accion">
          {abierto ? t('perfil.dispositivos.ocultar') : t('perfil.dispositivos.ver')}
          <FiChevronDown className="perfil-dispositivos-flecha" aria-hidden="true" />
        </span>
      </button>

      <div id={idRegion} className="perfil-dispositivos-cuerpo" hidden={!abierto}>
        {dispositivos?.length === 0 && <p className="campo-ayuda">{t('perfil.dispositivos.vacio')}</p>}

        {dispositivos?.length > 0 && (
          <ul className="perfil-dispositivos-lista">
            {dispositivos.map((d) => {
              const Icono = SISTEMAS_MOVILES.includes(d.sistema) ? FiSmartphone : FiMonitor;
              const nombre = d.navegador && d.sistema
                ? t('perfil.dispositivos.nombre', { navegador: d.navegador, sistema: d.sistema })
                : d.navegador || d.sistema || t('perfil.dispositivos.desconocido');
              return (
                <li key={d.id} className="perfil-dispositivo">
                  <span className="perfil-dispositivo-icono"><Icono aria-hidden="true" /></span>
                  <div className="perfil-dispositivo-datos">
                    <div className="perfil-dispositivo-nombre">
                      <strong>{nombre}</strong>
                      {d.actual && <span className="perfil-etiqueta perfil-etiqueta--actual">{t('perfil.dispositivos.esteDispositivo')}</span>}
                      {d.confiable_hasta && (
                        <span className="perfil-etiqueta perfil-etiqueta--confianza">
                          <FiShield aria-hidden="true" />
                          {t('perfil.dispositivos.confianzaHasta', { fecha: formatoFecha.format(new Date(d.confiable_hasta)) })}
                        </span>
                      )}
                    </div>
                    <p>
                      {d.sesion_activa
                        ? t('perfil.dispositivos.ultimoAcceso', { cuando: haceCuanto(d.ultimo_acceso || d.createdAt, i18n.language) })
                        : t('perfil.dispositivos.sinSesion')}
                    </p>
                    {d.ip && <p className="perfil-dispositivo-ip">{t('perfil.dispositivos.ip', { ip: d.ip })}</p>}
                  </div>
                  <button
                    type="button"
                    className="perfil-dispositivo-revocar"
                    onClick={() => revocar(d)}
                    disabled={procesando !== null}
                  >
                    {d.actual
                      ? t('perfil.dispositivos.cerrarAqui')
                      : d.sesion_activa ? t('perfil.dispositivos.cerrarSesion') : t('perfil.dispositivos.olvidar')}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {otros > 0 && (
          <div className="perfil-dispositivos-pie">
            <p>{t('perfil.dispositivos.ayudaTodos')}</p>
            <button
              type="button"
              className={`perfil-boton-peligro perfil-boton-peligro--suave${confirmandoTodos ? ' perfil-boton-peligro--confirmar' : ''}`}
              onClick={revocarTodos}
              disabled={procesando !== null}
            >
              {confirmandoTodos ? t('perfil.dispositivos.confirmarTodos') : t('perfil.dispositivos.cerrarTodos', { count: otros })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DispositivosConectados;
